import { trim } from "lodash";
import { CoreInterfaces } from "./../core/Models";
import * as Constants from "src/core/Constants";
import { fetchData, formContactPersonItemFromBEEmployee } from "src/utils";

export function fetchEngagementConfigurationData(
  businessOpportunityId: string,
  engagementConfigurationId: string = null
): Promise<CoreInterfaces.EngagementConfigurationReadPack> {
  return fetchData<CoreInterfaces.EngagementConfigurationReadPack>(
    `${Constants.APIPath.EngagementConfiguration}/${businessOpportunityId}${
      engagementConfigurationId ? "/" + engagementConfigurationId : ""
    }`
  );
}

export function fetchApplicationConfigurationData(): Promise<CoreInterfaces.ApplicationConfigurationReadPack> {
  return fetchData<CoreInterfaces.ApplicationConfigurationReadPack>(
    `${Constants.APIPath.ApplicationConfiguration}`
  );
}

export function fetchCustomerContactPersons(
  url: string
): Promise<Array<CoreInterfaces.ContactPersonItem>> {
  return fetchData<Array<CoreInterfaces.ContactPerson>>(`${url}`).then(
    (serverReturn: Array<CoreInterfaces.ContactPerson>) => {
      if (!!serverReturn) {
        return serverReturn.map((item) => ({
          key: `${item.ContactId}`,
          value: `${trim(`${item.FirstName} ${item.LastName}`)} <${
            item.Email
          }>`,
          untranslatable: true,
          email: item.Email,
          firstName: item.FirstName,
          lastName: item.LastName,
          office: null as string | null,
          businessUnit: null as string | null,
          jobName: null as string | null,
          GTSignee: {
            FirstName: item.FirstName,
            LastName: item.LastName,
            Email: item.Email,
            SigningMethod: Constants.SigningMethod.BankIDSE,
          },
        }));
      }
    }
  );
}

export function fetchGtContactPersons(): Promise<
  Array<CoreInterfaces.ContactPersonItem>
> {
  return fetchData<Array<CoreInterfaces.BEEmployee>>(
    Constants.APIPath.Employees
  ).then((serverReturn: Array<CoreInterfaces.BEEmployee>) => {
    if (!!serverReturn) {
      // The HR source contains duplicated and incomplete records. A repeated UID
      // collides as a React key, which corrupts how the option list renders.
      const seenUIDs = new Set<string>();
      return serverReturn.reduce<Array<CoreInterfaces.ContactPersonItem>>(
        (contactPersons, item) => {
          if (!item?.UID || seenUIDs.has(item.UID)) {
            return contactPersons;
          }
          seenUIDs.add(item.UID);

          const contactPersonItem = formContactPersonItemFromBEEmployee(item);
          if (!!contactPersonItem) {
            contactPersonItem.value = `${trim(
              `${item.FirstName} ${item.LastName}`
            )} <${item.Email}>, [${
              item.BusinessUnit === "BusinessConsultingSolutions"
                ? "BCS"
                : item.BusinessUnit
            }]`;
            contactPersons.push(contactPersonItem);
          }
          return contactPersons;
        },
        []
      );
    }
  });
}

export function fetchTeams(): Promise<Array<CoreInterfaces.Team>> {
  return fetchData<Array<CoreInterfaces.Team>>(
    Constants.APIPath.UserTeams
  ).then((serverReturn: Array<CoreInterfaces.Team>) => serverReturn);
}

export function fetchCompanyDetails(
  url: string
): Promise<CoreInterfaces.CompanyDetails> {
  return fetchData<CoreInterfaces.CompanyDetails>(`${url}`).then(
    (serverReturn: CoreInterfaces.CompanyDetails) => serverReturn
  );
}

// POST keeps the number (possibly a personal identity number) out of the URL.
export function searchCompaniesByOrgNr(
  orgNr: string
): Promise<Array<CoreInterfaces.CompanyDetails>> {
  return fetchData<Array<CoreInterfaces.CompanyDetails>>(
    Constants.APIPath.CompanySearch,
    Constants.APIMethod.POST,
    { OrgNr: orgNr }
  ).then((serverReturn) => serverReturn ?? []);
}

export function fetchBusinessOpportunityInfo(
  businessOpportunityId: string
): Promise<CoreInterfaces.BusinessOpportunityInfo> {
  return fetchData<CoreInterfaces.BusinessOpportunityInfo>(
    `${Constants.APIPath.BusinessOpportunityInfo}/${businessOpportunityId}`
  );
}

export function fetchSystemAdministrationMessages(): Promise<
  Array<CoreInterfaces.SystemAdministrationMessage>
> {
  return fetchData<Array<any>>(
    `${Constants.APIPath.NotificationMessages}`,
    Constants.APIMethod.GET
  )
    .then((data) =>
      data.map((item) => ({
        id: item.Id,
        title: item.Title,
        message: item.Content,
        link: item.Link,
        linkText: item.LinkText,
        type: item.Type,
        state: {
          isShown: true,
        },
      }))
    )
    .catch((): Array<CoreInterfaces.SystemAdministrationMessage> => []);
}

export function fetchEngagementLettersSummaryView(filterData: {}): Promise<CoreInterfaces.EngagementLettersSummary> {
  return fetchData<CoreInterfaces.EngagementLettersSummary>(
    Constants.APIPath.SearchEngagementLetters,
    Constants.APIMethod.POST,
    filterData
  );
}

export function fetchSignedLettersForOrganisation(
  organisationNumber: string
): Promise<Array<CoreInterfaces.SignedEngagementLetter>> {
  return fetchData<Array<CoreInterfaces.SignedEngagementLetter>>(
    Constants.APIPath.SignedLettersForOrganisation.replace(
      "{organisationNumber}",
      encodeURIComponent(organisationNumber)
    )
  );
}

export async function fetchUserRoles(): Promise<
  Array<Constants.UserRoles> | undefined
> {
  try {
    const roles = await fetchData<Array<Constants.UserRoles>>(
      Constants.APIPath.UserRoles
    );

    return roles;
  } catch (error) {
    return undefined;
  }
}

export async function fetchEmployeesOffices(): Promise<
  Array<CoreInterfaces.BEEmployee> | undefined
> {
  return fetchData<Array<CoreInterfaces.BEEmployee>>(
    Constants.APIPath.EmployeesOffices
  );
}

export async function fetchOfficeManagers(): Promise<
  Array<CoreInterfaces.OfficeManager> | undefined
> {
  try {
    const officeManager = await fetchData<Array<CoreInterfaces.OfficeManager>>(
      Constants.APIPath.OfficeManagers
    );

    return officeManager;
  } catch (error) {
    return undefined;
  }
}

export function checkMaintenanceMode(): Promise<CoreInterfaces.MaintenanceReadPack> {
  const url = `${Constants.APIPath.Maintenance}`;
  return fetchData<CoreInterfaces.MaintenanceReadPack>(url);
}
